import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import apiClient from '../api/client';
import { useTheme } from '../contexts/ThemeContext';
import { Upload, Download, Calendar, CheckCircle, XCircle, Clock, Users, DollarSign, Pencil, X, Save } from 'lucide-react';

const Attendance = () => {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [attendanceData, setAttendanceData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showSalaryUploadModal, setShowSalaryUploadModal] = useState(false);
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);
  const [showMarkAttendanceModal, setShowMarkAttendanceModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedSalaryFile, setSelectedSalaryFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [salaryUploadProgress, setSalaryUploadProgress] = useState(false);
  const [uploadStage, setUploadStage] = useState('');
  const [uploadPercent, setUploadPercent] = useState(0);
  const [salaryUploadStage, setSalaryUploadStage] = useState('');
  const [salaryUploadPercent, setSalaryUploadPercent] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [salaryMonth, setSalaryMonth] = useState('');
  const [attendanceSummary, setAttendanceSummary] = useState({});
  const [markingProgress, setMarkingProgress] = useState(false);
  const [showCycleSettingsModal, setShowCycleSettingsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [editDays, setEditDays] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [attendanceCycle, setAttendanceCycle] = useState({
    cycle_start_day: 1,
    cycle_name: 'Monthly'
  });
  const [attendanceForm, setAttendanceForm] = useState({
    employee_id: '',
    from_date: '',
    to_date: '',
    status: 'present',
    check_in: '',
    check_out: '',
    notes: ''
  });
  const [manualEntry, setManualEntry] = useState({
    employee_id: '',
    date: '',
    check_in: '',
    check_out: '',
    status: 'present',
    notes: ''
  });

  useEffect(() => {
    const currentDate = new Date();
    const monthStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(monthStr);
    setSalaryMonth(monthStr);
    fetchData(monthStr);
    fetchAttendanceCycle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (month) => {
    // Defensive: never query with an undefined/invalid month. Fall back to the
    // currently selected month (or the current calendar month) so a manual edit
    // save always refreshes against a valid cycle instead of blanking the grid.
    let targetMonth = month || selectedMonth;
    if (!targetMonth || !/^\d{4}-\d{2}$/.test(targetMonth)) {
      const now = new Date();
      targetMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    }
    try {
      const [attRes, empRes, reportRes] = await Promise.all([
        apiClient.get(`/api/attendance?month=${targetMonth}`),
        apiClient.get('/api/employees'),
        apiClient.get(`/api/reports/attendance?month=${targetMonth}`).catch(() => ({data: {}}))
      ]);
      setAttendanceData(attRes.data);
      setEmployees(empRes.data);
      setAttendanceSummary(reportRes.data);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceCycle = async () => {
    try {
      const response = await apiClient.get('/api/settings/attendance-cycle');
      setAttendanceCycle(response.data);
    } catch (error) {
      console.error('Error fetching attendance cycle:', error);
    }
  };

  const handleSaveAttendanceCycle = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/settings/attendance-cycle', attendanceCycle);
      alert('Attendance cycle updated successfully!');
      setShowCycleSettingsModal(false);
      fetchAttendanceCycle();
    } catch (error) {
      console.error('Error saving cycle:', error);
      alert('Error saving attendance cycle');
    }
  };


  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (!validExtensions.includes(fileExtension)) {
        alert('Please select an Excel (.xlsx, .xls) or CSV (.csv) file');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    setUploadProgress(true);
    setUploadStage('Uploading file...');
    setUploadPercent(0);
    
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      // Simulate upload progress
      const uploadInterval = setInterval(() => {
        setUploadPercent(prev => {
          if (prev < 30) return prev + 10;
          return prev;
        });
      }, 300);

      setUploadStage('Uploading file to server...');
      
      const response = await apiClient.post('/api/attendance/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 40) / progressEvent.total);
          setUploadPercent(percentCompleted);
        }
      });
      
      clearInterval(uploadInterval);
      
      // Processing stage
      setUploadStage('Processing attendance records...');
      setUploadPercent(50);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      setUploadPercent(70);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      setUploadPercent(90);
      
      await new Promise(resolve => setTimeout(resolve, 300));
      setUploadPercent(100);
      
      setUploadStage('Completed!');
      
      const d = response.data;
      // Switch the view to the month of the uploaded data so it reflects immediately.
      const targetMonth = (d.cycle_range && d.cycle_range.to)
        ? d.cycle_range.to.slice(0, 7)
        : selectedMonth;
      alert(
        `Success! Processed ${d.employees_processed} employee(s) across all sheets ` +
        `(${d.inserted} attendance records).` +
        (d.auto_created_employees > 0 ? `\nAdded ${d.auto_created_employees} new employee(s).` : '') +
        (d.excluded_duplicate_employees > 0
          ? `\nExcluded ${d.excluded_duplicate_employees} duplicate entry(ies).`
          : '') +
        (d.cycle_range && d.cycle_range.from
          ? `\n\nImported period: ${d.cycle_range.from} to ${d.cycle_range.to}.\nNow showing month: ${targetMonth}.`
          : '')
      );
      setSelectedMonth(targetMonth);
      fetchData(targetMonth);
      setShowUploadModal(false);
      setSelectedFile(null);
      setUploadPercent(0);
      setUploadStage('');
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setUploadProgress(false);
      setUploadPercent(0);
      setUploadStage('');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient.get('/api/attendance/template', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'attendance_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading template:', error);
    }
  };

  const handleManualEntry = async (e) => {
    e.preventDefault();
    try {
      // Find employee by ID to get emp_code
      const employee = employees.find(emp => emp.id === manualEntry.employee_id);
      if (!employee) {
        alert('Employee not found');
        return;
      }

      const attendanceData = {
        employee_id: employee.emp_code,  // Use emp_code for consistency
        date: manualEntry.date,
        check_in: manualEntry.check_in || null,
        check_out: manualEntry.check_out || null,
        status: manualEntry.status,
        notes: manualEntry.notes || 'Manually entered'
      };

      await apiClient.post('/api/attendance', attendanceData);
      alert('Attendance marked successfully!');
      fetchData(selectedMonth);
      setShowManualEntryModal(false);
      setManualEntry({
        employee_id: '',
        date: '',
        check_in: '',
        check_out: '',
        status: 'present',
        notes: ''
      });
    } catch (error) {
      console.error('Error marking attendance:', error);
      alert('Error marking attendance: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleSalaryFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (!validExtensions.includes(fileExtension)) {
        alert('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setSelectedSalaryFile(file);
    }
  };

  const handleBulkSalaryUpload = async () => {
    if (!selectedSalaryFile) {
      alert('Please select a salary statement file first');
      return;
    }

    if (!salaryMonth) {
      alert('Please select the salary month');
      return;
    }

    setSalaryUploadProgress(true);
    setSalaryUploadStage('Uploading salary statement...');
    setSalaryUploadPercent(10);
    
    const formData = new FormData();
    formData.append('file', selectedSalaryFile);

    try {
      setSalaryUploadPercent(30);
      setSalaryUploadStage('Processing salary components...');
      
      const response = await apiClient.post(`/api/payroll/bulk-salary-upload?month=${salaryMonth}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 30) / progressEvent.total) + 30;
          setSalaryUploadPercent(percentCompleted);
        }
      });
      
      setSalaryUploadPercent(70);
      setSalaryUploadStage('Creating payroll records...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSalaryUploadPercent(90);
      setSalaryUploadStage('Finalizing...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSalaryUploadPercent(100);
      setSalaryUploadStage('Completed!');
      
      alert(`Success! Processed ${response.data.total} employees. ${response.data.inserted} new, ${response.data.updated} updated.`);
      setShowSalaryUploadModal(false);
      setSelectedSalaryFile(null);
      setSalaryMonth('');
    } catch (error) {
      console.error('Error uploading salary file:', error);
      alert('Error uploading salary file: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSalaryUploadProgress(false);
      setSalaryUploadPercent(0);
      setSalaryUploadStage('');
    }
  };

  const handleDownloadSalaryTemplate = async () => {
    try {
      const response = await apiClient.get('/api/payroll/salary-template', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'salary_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading template:', error);
    }
  };

  const handleMarkAttendance = async (e) => {
    e.preventDefault();
    
    if (!attendanceForm.from_date || !attendanceForm.to_date || !attendanceForm.employee_id) {
      alert('Please fill all required fields');
      return;
    }

    setMarkingProgress(true);

    try {
      // Find employee to get emp_code
      const employee = employees.find(emp => emp.id === attendanceForm.employee_id);
      if (!employee) {
        alert('Employee not found');
        return;
      }

      // Generate all dates between from_date and to_date
      const fromDate = new Date(attendanceForm.from_date);
      const toDate = new Date(attendanceForm.to_date);
      const dates = [];
      
      for (let dt = new Date(fromDate); dt <= toDate; dt.setDate(dt.getDate() + 1)) {
        dates.push(new Date(dt).toISOString().split('T')[0]);
      }

      // Mark attendance for each date
      let marked = 0;
      for (const date of dates) {
        const attendanceData = {
          employee_id: employee.emp_code,
          date: date,
          check_in: attendanceForm.check_in || null,
          check_out: attendanceForm.check_out || null,
          status: attendanceForm.status,
          notes: attendanceForm.notes || `Manually marked for ${dates.length} days`
        };

        await apiClient.post('/api/attendance', attendanceData);
        marked++;
      }

      alert(`✅ Successfully marked attendance for ${marked} days (${attendanceForm.from_date} to ${attendanceForm.to_date})`);
      fetchData(selectedMonth);
      setShowMarkAttendanceModal(false);
      setAttendanceForm({
        employee_id: '',
        from_date: '',
        to_date: '',
        status: 'present',
        check_in: '',
        check_out: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error marking attendance:', error);
      alert('Error marking attendance: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setMarkingProgress(false);
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? emp.full_name : empId;
  };

  const getEmployeeSummary = (empId) => {
    const summary = attendanceSummary[empId];
    if (!summary) return { present: 0, absent: 0, half_day: 0, leave: 0, paid_leave_available: 0, paid_leave_days: 0 };
    return summary;
  };

  // Build the list of dates in the currently selected cycle for the edit modal
  const buildCycleDates = () => {
    if (!selectedMonth) return [];
    const [y, m] = selectedMonth.split('-').map(Number);
    const startDay = attendanceCycle.cycle_start_day || 1;
    let start, end;
    if (startDay === 1) {
      start = new Date(y, m - 1, 1);
      end = new Date(y, m, 0);
    } else {
      start = new Date(y, m - 2, startDay);
      end = new Date(y, m - 1, startDay - 1);
    }
    const dates = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().slice(0, 10));
    }
    return dates;
  };

  const openEditModal = async (emp) => {
    setEditEmployee(emp);
    setShowEditModal(true);
    setEditLoading(true);
    try {
      const res = await apiClient.get(`/api/attendance?employee_id=${emp.emp_code}&month=${selectedMonth}`);
      const byDate = {};
      res.data.forEach((r) => { byDate[r.date] = r; });
      const days = buildCycleDates().map((date) => ({
        date,
        status: byDate[date]?.status || 'absent',
        check_in: byDate[date]?.check_in || '',
        check_out: byDate[date]?.check_out || '',
      }));
      setEditDays(days);
    } catch (err) {
      alert('Failed to load attendance: ' + (err.response?.data?.detail || err.message));
    } finally {
      setEditLoading(false);
    }
  };

  const updateEditDay = (idx, field, value) => {
    setEditDays((prev) => prev.map((d, i) => (i === idx ? { ...d, [field]: value } : d)));
  };

  const saveEditAttendance = async () => {
    if (!editEmployee) return;
    setEditSaving(true);
    try {
      await apiClient.post('/api/attendance/bulk-edit', {
        employee_id: editEmployee.emp_code,
        records: editDays.map((d) => ({
          date: d.date,
          status: d.status,
          check_in: d.check_in || null,
          check_out: d.check_out || null,
        })),
      });
      alert('Attendance updated for ' + editEmployee.full_name);
      setShowEditModal(false);
      setEditEmployee(null);
      fetchData(selectedMonth);
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.detail || err.message));
    } finally {
      setEditSaving(false);
    }
  };

  const dayOfWeek = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
  };

  if (loading) {
    return (
      <Layout>
        <div className='flex items-center justify-center h-full'>
          <div className='loading-spinner'></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='page-header flex items-center justify-between'>
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>Attendance Management</h1>
          <p className={`mt-1 ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-600'}`}>Track and manage employee attendance from ESSL biometric</p>
        </div>
        <div className='flex gap-3'>
          <button
            onClick={() => setShowCycleSettingsModal(true)}
            className='flex items-center gap-2 bg-[#00B8B8] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#0ED2D2] transition-all'
          >
            <Calendar size={18} />
            Cycle Settings
          </button>
          <button
            onClick={() => setShowMarkAttendanceModal(true)}
            data-testid='mark-attendance-button'
            className='flex items-center gap-2 bg-[#00B8B8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0ED2D2] transition-all'
          >
            <Calendar size={20} />
            Mark Attendance
          </button>
          <button
            onClick={() => setShowSalaryUploadModal(true)}
            data-testid='bulk-salary-upload-button'
            className='flex items-center gap-2 bg-[#10B981] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#059669] transition-all'
          >
            <DollarSign size={20} />
            Bulk Salary Upload
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            data-testid='upload-attendance-button'
            className='flex items-center gap-2 bg-[#7EC845] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#6EB835] transition-all'
          >
            <Upload size={20} />
            Upload Biometric Data
          </button>
        </div>
      </div>

      <div className='bg-white rounded-xl border border-[#E5E5E5] p-4 mb-6'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-4'>
            <label className='text-sm font-medium text-[#262626]'>Select Month:</label>
            <input
              type='month'
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                fetchData(e.target.value);
              }}
              data-testid='attendance-month-select'
              className='px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
            />
          </div>
          <div className='text-sm text-[#737373]'>
            <strong>Attendance Cycle:</strong> {attendanceCycle.cycle_name} (Day {attendanceCycle.cycle_start_day})
          </div>
        </div>
      </div>

      <div className='border-b border-[#E5E5E5] mb-6'>
        <div className='flex gap-6'>
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 px-2 font-medium transition-colors ${activeTab === 'overview' ? 'text-[#7EC845] border-b-2 border-[#7EC845]' : 'text-[#737373] hover:text-[#262626]'}`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('records')}
            className={`pb-3 px-2 font-medium transition-colors ${activeTab === 'records' ? 'text-[#7EC845] border-b-2 border-[#7EC845]' : 'text-[#737373] hover:text-[#262626]'}`}
          >
            Daily Records
          </button>
        </div>
      </div>

      {activeTab === 'overview' && (
        <div>
          <div className='grid grid-cols-1 md:grid-cols-5 gap-6 mb-6'>
            <div className='bg-white rounded-xl border border-[#E5E5E5] p-6'>
              <div className='flex items-center justify-between mb-2'>
                <Users className='text-[#7EC845]' size={24} />
                <span className='text-2xl font-bold text-[#262626]'>{employees.length}</span>
              </div>
              <p className='text-sm text-[#737373]'>Total Employees</p>
            </div>
            <div className='bg-white rounded-xl border border-[#E5E5E5] p-6'>
              <div className='flex items-center justify-between mb-2'>
                <CheckCircle className='text-[#10B981]' size={24} />
                <span className='text-2xl font-bold text-[#262626]'>{attendanceData.filter(a => a.status === 'present').length}</span>
              </div>
              <p className='text-sm text-[#737373]'>Present Records</p>
            </div>
            <div className='bg-white rounded-xl border border-[#E5E5E5] p-6'>
              <div className='flex items-center justify-between mb-2'>
                <XCircle className='text-[#EF4444]' size={24} />
                <span className='text-2xl font-bold text-[#262626]'>{attendanceData.filter(a => a.status === 'absent').length}</span>
              </div>
              <p className='text-sm text-[#737373]'>Absent Records</p>
            </div>
            <div className='bg-white rounded-xl border border-[#E5E5E5] p-6'>
              <div className='flex items-center justify-between mb-2'>
                <Clock className='text-[#F59E0B]' size={24} />
                <span className='text-2xl font-bold text-[#262626]'>{attendanceData.filter(a => a.status === 'half_day').length}</span>
              </div>
              <p className='text-sm text-[#737373]'>Half Days</p>
            </div>
            <div className='bg-white rounded-xl border border-[#E5E5E5] p-6'>
              <div className='flex items-center justify-between mb-2'>
                <Calendar className='text-[#0EA5E9]' size={24} />
                <span className='text-2xl font-bold text-[#262626]'>{attendanceData.filter(a => a.status === 'weekly_off').length}</span>
              </div>
              <p className='text-sm text-[#737373]'>Weekly Off (Sat/WO)</p>
            </div>
          </div>

          <div className='bg-white rounded-xl border border-[#E5E5E5]'>
            <div className='p-6 border-b border-[#E5E5E5]'>
              <h3 className='text-lg font-semibold text-[#262626]'>Employee-wise Attendance Summary</h3>
            </div>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-[#F5F5F5]'>
                  <tr>
                    <th className='text-left p-4 font-medium text-[#262626]'>Employee</th>
                    <th className='text-left p-4 font-medium text-[#262626]'>Present</th>
                    <th className='text-left p-4 font-medium text-[#262626]'>Absent</th>
                    <th className='text-left p-4 font-medium text-[#262626]'>Half Day</th>
                    <th className='text-left p-4 font-medium text-[#262626]'>Weekly Off</th>
                    <th className='text-left p-4 font-medium text-[#262626]'>Payable Days</th>
                    <th className='text-left p-4 font-medium text-[#262626]'>Paid Leave</th>
                    <th className='text-left p-4 font-medium text-[#262626]'>Attendance %</th>
                    <th className='text-left p-4 font-medium text-[#262626]'>Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {[...employees].sort((a, b) => {
                    const sa = getEmployeeSummary(a.id);
                    const sb = getEmployeeSummary(b.id);
                    const ta = (sa.present || 0) + (sa.absent || 0) + (sa.half_day || 0) + (sa.leave || 0) + (sa.weekly_off || 0);
                    const tb = (sb.present || 0) + (sb.absent || 0) + (sb.half_day || 0) + (sb.leave || 0) + (sb.weekly_off || 0);
                    return tb - ta; // employees WITH data for this month first
                  }).map((emp) => {
                    const summary = getEmployeeSummary(emp.id);
                    const weeklyOff = summary.weekly_off || 0;
                    const total = summary.present + summary.absent + summary.half_day + summary.leave + weeklyOff;
                    const percentage = total > 0 ? ((summary.present + summary.half_day * 0.5) / total * 100).toFixed(1) : 0;
                    
                    return (
                      <tr key={emp.id} className='border-t border-[#E5E5E5] hover:bg-[#F5F5F5]'>
                        <td className='p-4 font-medium text-[#262626]'>{emp.full_name}</td>
                        <td className='p-4 text-[#10B981] font-mono'>{summary.present || 0}</td>
                        <td className='p-4 text-[#EF4444] font-mono'>{summary.absent || 0}</td>
                        <td className='p-4 text-[#F59E0B] font-mono'>{summary.half_day || 0}</td>
                        <td className='p-4 text-[#0EA5E9] font-mono'>{weeklyOff}</td>
                        <td className='p-4 text-[#262626] font-mono font-semibold'>{summary.payable_days != null ? summary.payable_days : 0}</td>
                        <td className='p-4 font-mono' data-testid={`paid-leave-${emp.emp_code}`}>
                          <span className='px-2 py-1 rounded-full text-xs font-semibold bg-[#CCFBF1] text-[#0F766E]' title={`Earned ${summary.paid_leave_earned || 0}, Used ${summary.paid_leave_used || 0}`}>
                            {summary.paid_leave_available != null ? summary.paid_leave_available : 0} avail
                          </span>
                          {(summary.paid_leave_days || 0) > 0 && (
                            <span className='ml-1 text-xs text-[#0EA5E9]'>({summary.paid_leave_days} this cycle)</span>
                          )}
                        </td>
                        <td className='p-4'>
                          <div className='flex items-center gap-2'>
                            <div className='flex-1 bg-[#F5F5F5] rounded-full h-2'>
                              <div className='bg-[#7EC845] h-2 rounded-full transition-all' style={{ width: `${percentage}%` }}></div>
                            </div>
                            <span className='font-mono text-sm font-medium text-[#262626]'>{percentage}%</span>
                          </div>
                        </td>
                        <td className='p-4'>
                          <button
                            onClick={() => openEditModal(emp)}
                            data-testid={`edit-attendance-${emp.emp_code}`}
                            title='Edit attendance'
                            className='p-2 rounded-lg text-[#0EA5E9] hover:bg-[#E0F2FE] transition-colors'
                          >
                            <Pencil size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'records' && (
        <div className='bg-white rounded-xl border border-[#E5E5E5]'>
          <div className='p-6 border-b border-[#E5E5E5]'>
            <h3 className='text-lg font-semibold text-[#262626]'>Daily Attendance Records</h3>
          </div>
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead className='bg-[#F5F5F5]'>
                <tr>
                  <th className='text-left p-4 font-medium text-[#262626]'>Employee</th>
                  <th className='text-left p-4 font-medium text-[#262626]'>Date</th>
                  <th className='text-left p-4 font-medium text-[#262626]'>Check In</th>
                  <th className='text-left p-4 font-medium text-[#262626]'>Check Out</th>
                  <th className='text-left p-4 font-medium text-[#262626]'>Status</th>
                  <th className='text-left p-4 font-medium text-[#262626]'>Notes</th>
                </tr>
              </thead>
              <tbody>
                {attendanceData.length === 0 ? (
                  <tr><td colSpan='6' className='text-center p-8 text-[#737373]'>No attendance records found for selected month</td></tr>
                ) : (
                  attendanceData.map((record, idx) => (
                    <tr key={record.id || `${record.employee_id}-${record.date}` || idx} className='border-t border-[#E5E5E5] hover:bg-[#F5F5F5]'>
                      <td className='p-4 font-medium text-[#262626]'>{getEmployeeName(record.employee_id)}</td>
                      <td className='p-4 text-[#737373]'>{record.date}</td>
                      <td className='p-4 font-mono text-[#262626]'>{record.check_in || '-'}</td>
                      <td className='p-4 font-mono text-[#262626]'>{record.check_out || '-'}</td>
                      <td className='p-4'>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          record.status === 'present' ? 'bg-green-100 text-green-800' :
                          record.status === 'absent' ? 'bg-red-100 text-red-800' :
                          record.status === 'half_day' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                        }`}>{record.status.replace('_', ' ').toUpperCase()}</span>
                      </td>
                      <td className='p-4 text-sm text-[#737373]'>{record.notes || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50' onClick={() => setShowUploadModal(false)}>
          <div className='bg-white rounded-xl p-6 max-w-2xl w-full m-4' onClick={(e) => e.stopPropagation()}>
            <h2 className='text-2xl font-bold text-[#262626] mb-6'>Upload Biometric Attendance Data</h2>
            
            <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6'>
              <h4 className='font-semibold text-blue-900 mb-2'>ESSL Biometric Machine Support</h4>
              <p className='text-sm text-blue-800 mb-2'>The system auto-detects the biometric export format on upload:</p>
              <ul className='text-sm text-blue-800 list-disc list-inside space-y-1'>
                <li><strong>BasicWorkDuration (Monthly Status Report):</strong> standard ESSL export - auto-detected</li>
                <li><strong>WOP handling:</strong> a weekly-off is counted Present only if a punch time exists, otherwise Absent</li>
                <li><strong>Multiple uploads:</strong> new employees are imported; employees already present for the same period are excluded (no duplicates)</li>
              </ul>
            </div>

            <div className='space-y-4'>
              <div>
                <p className='text-sm text-[#737373] mb-4'>Upload Excel or CSV file with attendance data from your ESSL biometric machine. Download the template to see supported formats.</p>
                <button onClick={handleDownloadTemplate} data-testid='download-attendance-template' className='flex items-center gap-2 px-4 py-2 border border-[#7EC845] text-[#7EC845] rounded-lg hover:bg-[#7EC845]/10 mb-4'>
                  <Download size={18} />Download Template (Excel)
                </button>
              </div>
              
              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Select File (Excel or CSV)</label>
                <input type='file' accept='.xlsx,.xls,.csv' onChange={handleFileSelect} data-testid='attendance-file-input' className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg' />
                {selectedFile && (
                  <div className='mt-3 p-3 bg-green-50 border border-green-200 rounded-lg'>
                    <p className='text-sm text-green-800'><strong>Selected:</strong> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)</p>
                  </div>
                )}
              </div>

              <div className='bg-[#F5F5F5] rounded-lg p-4'>
                <p className='text-sm font-medium text-[#262626] mb-2'>How to export from ESSL machine:</p>
                <ol className='text-sm text-[#737373] list-decimal list-inside space-y-1'>
                  <li>Connect your ESSL device to computer via USB/Network</li>
                  <li>Open eTime TrackLite software</li>
                  <li>Go to Export Logs → Attendance Logs</li>
                  <li>Select date range and click Generate</li>
                  <li>Export as Excel or CSV format</li>
                  <li>Upload the exported file here</li>
                </ol>
              </div>

              <div className='flex gap-4 justify-end mt-6'>
                <button
                  type='button'
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                    setUploadPercent(0);
                    setUploadStage('');
                  }}
                  disabled={uploadProgress}
                  className='px-6 py-2 border border-[#E5E5E5] rounded-lg text-[#262626] hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUpload}
                  disabled={!selectedFile || uploadProgress}
                  data-testid='submit-attendance-upload'
                  className='px-6 py-2 bg-[#7EC845] text-white rounded-lg hover:bg-[#6EB835] disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {uploadProgress ? 'Uploading...' : 'Upload Attendance'}
                </button>
              </div>

              {uploadProgress && (
                <div className='mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
                  <div className='flex items-center justify-between mb-2'>
                    <span className='text-sm font-medium text-blue-900'>{uploadStage}</span>
                    <span className='text-sm font-bold text-blue-900'>{uploadPercent}%</span>
                  </div>
                  <div className='w-full bg-blue-200 rounded-full h-3 overflow-hidden'>
                    <div
                      className='bg-[#7EC845] h-3 rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-2'
                      style={{ width: `${uploadPercent}%` }}
                    >
                      {uploadPercent > 10 && (
                        <span className='text-xs text-white font-bold'>{uploadPercent}%</span>
                      )}
                    </div>
                  </div>
                  <p className='text-xs text-blue-700 mt-2'>Please wait while we process your attendance data...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manual Entry Modal */}
      {showManualEntryModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50' onClick={() => setShowManualEntryModal(false)}>
          <div className='bg-white rounded-xl p-6 max-w-2xl w-full m-4' onClick={(e) => e.stopPropagation()}>
            <h2 className='text-2xl font-bold text-[#262626] mb-6'>Mark Attendance Manually</h2>
            
            <form onSubmit={handleManualEntry} className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Employee*</label>
                <select
                  required
                  value={manualEntry.employee_id}
                  onChange={(e) => setManualEntry({...manualEntry, employee_id: e.target.value})}
                  data-testid='manual-employee-select'
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                >
                  <option value=''>Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.emp_code} - {emp.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-[#262626] mb-2'>Date*</label>
                  <input
                    type='date'
                    required
                    value={manualEntry.date}
                    onChange={(e) => setManualEntry({...manualEntry, date: e.target.value})}
                    data-testid='manual-date-input'
                    className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-[#262626] mb-2'>Status*</label>
                  <select
                    required
                    value={manualEntry.status}
                    onChange={(e) => setManualEntry({...manualEntry, status: e.target.value})}
                    data-testid='manual-status-select'
                    className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                  >
                    <option value='present'>Present</option>
                    <option value='absent'>Absent</option>
                    <option value='half_day'>Half Day</option>
                    <option value='leave'>Leave</option>
                  </select>
                </div>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-[#262626] mb-2'>Check In Time</label>
                  <input
                    type='time'
                    value={manualEntry.check_in}
                    onChange={(e) => setManualEntry({...manualEntry, check_in: e.target.value})}
                    data-testid='manual-checkin-input'
                    className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                    placeholder='HH:MM'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-[#262626] mb-2'>Check Out Time</label>
                  <input
                    type='time'
                    value={manualEntry.check_out}
                    onChange={(e) => setManualEntry({...manualEntry, check_out: e.target.value})}
                    data-testid='manual-checkout-input'
                    className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                    placeholder='HH:MM'
                  />
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Notes</label>
                <textarea
                  value={manualEntry.notes}
                  onChange={(e) => setManualEntry({...manualEntry, notes: e.target.value})}
                  rows='2'
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                  placeholder='Optional notes about this attendance record'
                ></textarea>
              </div>

              <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
                <p className='text-sm text-yellow-800'>
                  <strong>Note:</strong> Manual entry will update existing record if attendance already marked for this date. 
                  For bulk uploads from ESSL machine, use &quot;Upload Biometric Data&quot; button.
                </p>
              </div>

              <div className='flex gap-4 justify-end mt-6'>
                <button
                  type='button'
                  onClick={() => setShowManualEntryModal(false)}
                  className='px-6 py-2 border border-[#E5E5E5] rounded-lg text-[#262626] hover:bg-[#F5F5F5]'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  data-testid='submit-manual-attendance'
                  className='px-6 py-2 bg-[#7EC845] text-white rounded-lg hover:bg-[#6EB835]'
                >
                  Mark Attendance
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Salary Upload Modal */}
      {showSalaryUploadModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50' onClick={() => setShowSalaryUploadModal(false)}>
          <div className='bg-white rounded-xl p-6 max-w-2xl w-full m-4' onClick={(e) => e.stopPropagation()}>
            <h2 className='text-2xl font-bold text-[#262626] mb-6'>Bulk Salary Upload</h2>
            
            <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6'>
              <h4 className='font-semibold text-blue-900 mb-2'>Upload Salary Statement</h4>
              <p className='text-sm text-blue-800 mb-2'>Upload an Excel file containing salary components for multiple employees.</p>
              <p className='text-sm text-blue-800'>Required columns: Employee Code, Basic Salary, HRA, Conveyance, Medical, Special Allowance, PF, ESI, Professional Tax, TDS</p>
            </div>

            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Salary Month*</label>
                <input
                  type='month'
                  value={salaryMonth}
                  onChange={(e) => setSalaryMonth(e.target.value)}
                  data-testid='salary-month-input'
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#1B5E4A]'
                  required
                />
              </div>

              <div>
                <p className='text-sm text-[#737373] mb-4'>Upload Excel file with salary components. Download the template to see the required format.</p>
                <button 
                  onClick={handleDownloadSalaryTemplate} 
                  data-testid='download-salary-template' 
                  className='flex items-center gap-2 px-4 py-2 border border-[#1B5E4A] text-[#1B5E4A] rounded-lg hover:bg-[#1B5E4A]/10 mb-4'
                >
                  <Download size={18} />Download Salary Template (Excel)
                </button>
              </div>
              
              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Select Salary File (Excel)</label>
                <input 
                  type='file' 
                  accept='.xlsx,.xls' 
                  onChange={handleSalaryFileSelect} 
                  data-testid='salary-file-input' 
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg' 
                />
                {selectedSalaryFile && (
                  <div className='mt-3 p-3 bg-green-50 border border-green-200 rounded-lg'>
                    <p className='text-sm text-green-800'><strong>Selected:</strong> {selectedSalaryFile.name} ({(selectedSalaryFile.size / 1024).toFixed(2)} KB)</p>
                  </div>
                )}
              </div>

              <div className='bg-[#F5F5F5] rounded-lg p-4'>
                <p className='text-sm font-medium text-[#262626] mb-2'>Template Format:</p>
                <ul className='text-sm text-[#737373] list-disc list-inside space-y-1'>
                  <li>Employee Code (must match existing employees)</li>
                  <li>Basic Salary, HRA, Conveyance, Medical Allowance</li>
                  <li>Special Allowance, Other Allowances</li>
                  <li>PF Deduction, ESI Deduction, Professional Tax, TDS</li>
                  <li>All amounts should be numeric values</li>
                </ul>
              </div>

              <div className='flex gap-4 justify-end mt-6'>
                <button
                  type='button'
                  onClick={() => {
                    setShowSalaryUploadModal(false);
                    setSelectedSalaryFile(null);
                    setSalaryMonth('');
                    setSalaryUploadPercent(0);
                    setSalaryUploadStage('');
                  }}
                  disabled={salaryUploadProgress}
                  className='px-6 py-2 border border-[#E5E5E5] rounded-lg text-[#262626] hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkSalaryUpload}
                  disabled={!selectedSalaryFile || !salaryMonth || salaryUploadProgress}
                  data-testid='submit-salary-upload'
                  className='px-6 py-2 bg-[#1B5E4A] text-white rounded-lg hover:bg-[#145442] disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {salaryUploadProgress ? 'Uploading...' : 'Upload Salary Data'}
                </button>
              </div>

              {salaryUploadProgress && (
                <div className='mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
                  <div className='flex items-center justify-between mb-2'>
                    <span className='text-sm font-medium text-blue-900'>{salaryUploadStage}</span>
                    <span className='text-sm font-bold text-blue-900'>{salaryUploadPercent}%</span>
                  </div>
                  <div className='w-full bg-blue-200 rounded-full h-3 overflow-hidden'>
                    <div
                      className='bg-[#1B5E4A] h-3 rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-2'
                      style={{ width: `${salaryUploadPercent}%` }}
                    >
                      {salaryUploadPercent > 10 && (
                        <span className='text-xs text-white font-bold'>{salaryUploadPercent}%</span>
                      )}
                    </div>
                  </div>
                  <p className='text-xs text-blue-700 mt-2'>Please wait while we process your salary data...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mark Attendance Modal */}
      {showMarkAttendanceModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50' onClick={() => setShowMarkAttendanceModal(false)}>
          <div className='bg-white rounded-xl p-6 max-w-2xl w-full m-4' onClick={(e) => e.stopPropagation()}>
            <h2 className='text-2xl font-bold text-[#262626] mb-6'>Mark Attendance (Date Range)</h2>
            
            <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6'>
              <p className='text-sm text-blue-800'>
                Mark attendance for an employee across multiple days. Select the date range and status to apply to all days.
              </p>
            </div>

            <form onSubmit={handleMarkAttendance} className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Employee*</label>
                <select
                  required
                  value={attendanceForm.employee_id}
                  onChange={(e) => setAttendanceForm({...attendanceForm, employee_id: e.target.value})}
                  data-testid='mark-employee-select'
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#0080FF]'
                >
                  <option value=''>Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.emp_code} - {emp.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-[#262626] mb-2'>From Date*</label>
                  <input
                    type='date'
                    required
                    value={attendanceForm.from_date}
                    onChange={(e) => setAttendanceForm({...attendanceForm, from_date: e.target.value})}
                    data-testid='mark-from-date-input'
                    className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#0080FF]'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-[#262626] mb-2'>To Date*</label>
                  <input
                    type='date'
                    required
                    value={attendanceForm.to_date}
                    onChange={(e) => setAttendanceForm({...attendanceForm, to_date: e.target.value})}
                    data-testid='mark-to-date-input'
                    className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#0080FF]'
                  />
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Status*</label>
                <select
                  required
                  value={attendanceForm.status}
                  onChange={(e) => setAttendanceForm({...attendanceForm, status: e.target.value})}
                  data-testid='mark-status-select'
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#0080FF]'
                >
                  <option value='present'>Present</option>
                  <option value='absent'>Absent</option>
                  <option value='half_day'>Half Day</option>
                  <option value='leave'>Leave</option>
                </select>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-[#262626] mb-2'>Check In Time (Optional)</label>
                  <input
                    type='time'
                    value={attendanceForm.check_in}
                    onChange={(e) => setAttendanceForm({...attendanceForm, check_in: e.target.value})}
                    className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#0080FF]'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-[#262626] mb-2'>Check Out Time (Optional)</label>
                  <input
                    type='time'
                    value={attendanceForm.check_out}
                    onChange={(e) => setAttendanceForm({...attendanceForm, check_out: e.target.value})}
                    className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#0080FF]'
                  />
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Notes</label>
                <textarea
                  value={attendanceForm.notes}
                  onChange={(e) => setAttendanceForm({...attendanceForm, notes: e.target.value})}
                  rows='2'
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#0080FF]'
                  placeholder='Optional notes for this attendance period'
                ></textarea>
              </div>

              <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
                <p className='text-sm text-yellow-800'>
                  <strong>Note:</strong> This will mark attendance for ALL days between the selected dates. 
                  Existing records will be updated. This is useful for marking leave periods or backdating attendance.
                </p>
              </div>

              <div className='flex gap-4 justify-end mt-6'>
                <button
                  type='button'
                  onClick={() => setShowMarkAttendanceModal(false)}
                  disabled={markingProgress}
                  className='px-6 py-2 border border-[#E5E5E5] rounded-lg text-[#262626] hover:bg-[#F5F5F5] disabled:opacity-50'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={markingProgress}
                  data-testid='submit-mark-attendance'
                  className='px-6 py-2 bg-[#0080FF] text-white rounded-lg hover:bg-[#0070DD] disabled:opacity-50'
                >
                  {markingProgress ? 'Marking...' : 'Mark Attendance'}
                </button>
              </div>

              {markingProgress && (
                <div className='mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
                  <div className='flex items-center gap-3'>
                    <div className='loading-spinner'></div>
                    <span className='text-sm text-blue-900'>Marking attendance for selected date range...</span>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Attendance Cycle Settings Modal */}
      {showCycleSettingsModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50' onClick={() => setShowCycleSettingsModal(false)}>
          <div className='bg-white rounded-xl p-6 max-w-lg w-full m-4' onClick={(e) => e.stopPropagation()}>
            <h2 className='text-2xl font-bold text-[#262626] mb-6'>Attendance Cycle Settings</h2>
            
            <form onSubmit={handleSaveAttendanceCycle} className='space-y-4'>
              <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
                <p className='text-sm text-blue-800'>
                  Define when your attendance month starts. For example, if you run payroll from 26th of one month to 25th of next month, 
                  set cycle start to <strong>26</strong>.
                </p>
              </div>

              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Cycle Start Day*</label>
                <input
                  type='number'
                  required
                  min='1'
                  max='28'
                  value={attendanceCycle.cycle_start_day}
                  onChange={(e) => setAttendanceCycle({...attendanceCycle, cycle_start_day: parseInt(e.target.value)})}
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                  placeholder='26'
                />
                <p className='text-xs text-[#737373] mt-1'>
                  Day of month when attendance cycle starts (1-28). Example: 26 for "26th to 25th" cycle
                </p>
              </div>

              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Cycle Name</label>
                <input
                  type='text'
                  value={attendanceCycle.cycle_name}
                  onChange={(e) => setAttendanceCycle({...attendanceCycle, cycle_name: e.target.value})}
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                  placeholder='e.g., 26th to 25th Cycle'
                />
              </div>

              <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
                <p className='text-sm text-yellow-800'>
                  <strong>Example:</strong> If you set start day to <strong>26</strong>, then:
                </p>
                <ul className='text-sm text-yellow-800 mt-2 space-y-1'>
                  <li>• November cycle: 26-Nov to 25-Dec → Shows in <strong>December payroll</strong></li>
                  <li>• December cycle: 26-Dec to 25-Jan → Shows in <strong>January payroll</strong></li>
                </ul>
              </div>

              <div className='flex gap-4 justify-end mt-6'>
                <button
                  type='button'
                  onClick={() => setShowCycleSettingsModal(false)}
                  className='px-6 py-2 border border-[#E5E5E5] rounded-lg text-[#262626] hover:bg-[#F5F5F5]'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='px-6 py-2 bg-[#7EC845] text-white rounded-lg hover:bg-[#6EB835]'
                >
                  Save Cycle Settings
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Individual Attendance Edit Modal */}
      {showEditModal && editEmployee && (
        <div className='fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4' data-testid='edit-attendance-modal'>
          <div className='bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col'>
            <div className='flex items-center justify-between p-6 border-b border-[#E5E5E5]'>
              <div>
                <h3 className='text-lg font-semibold text-[#262626]'>Edit Attendance</h3>
                <p className='text-sm text-[#737373]'>{editEmployee.full_name} ({editEmployee.emp_code}) &middot; {selectedMonth}</p>
              </div>
              <button onClick={() => setShowEditModal(false)} className='p-2 rounded-lg hover:bg-[#F5F5F5] text-[#737373]'>
                <X size={20} />
              </button>
            </div>
            <div className='p-6 overflow-y-auto flex-1'>
              {editLoading ? (
                <div className='flex items-center justify-center py-10'><div className='loading-spinner'></div></div>
              ) : (
                <div className='space-y-2'>
                  {editDays.map((day, idx) => (
                    <div key={day.date} className='flex items-center gap-3 py-2 border-b border-[#F0F0F0]'>
                      <div className='w-32 text-sm text-[#262626] font-mono'>
                        {day.date} <span className='text-[#A3A3A3]'>{dayOfWeek(day.date)}</span>
                      </div>
                      <select
                        value={day.status}
                        onChange={(e) => updateEditDay(idx, 'status', e.target.value)}
                        data-testid={`edit-day-status-${day.date}`}
                        className='flex-1 px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#7EC845]'
                      >
                        <option value='present'>Present</option>
                        <option value='absent'>Absent</option>
                        <option value='half_day'>Half Day</option>
                        <option value='paid_leave'>Paid Leave</option>
                        <option value='weekly_off'>Weekly Off (Sat/WO)</option>
                        <option value='leave'>Leave</option>
                        <option value='holiday'>Holiday</option>
                      </select>
                      <input
                        type='text'
                        value={day.check_in || ''}
                        onChange={(e) => updateEditDay(idx, 'check_in', e.target.value)}
                        placeholder='In'
                        className='w-20 px-2 py-2 border border-[#E5E5E5] rounded-lg text-sm text-center'
                      />
                      <input
                        type='text'
                        value={day.check_out || ''}
                        onChange={(e) => updateEditDay(idx, 'check_out', e.target.value)}
                        placeholder='Out'
                        className='w-20 px-2 py-2 border border-[#E5E5E5] rounded-lg text-sm text-center'
                      />
                    </div>
                  ))}
                  {editDays.length === 0 && (
                    <p className='text-center text-[#737373] py-6'>Select a month first to edit attendance.</p>
                  )}
                </div>
              )}
            </div>
            <div className='flex items-center justify-end gap-3 p-6 border-t border-[#E5E5E5]'>
              <button onClick={() => setShowEditModal(false)} className='px-4 py-2 rounded-lg border border-[#E5E5E5] text-[#262626] hover:bg-[#F5F5F5]'>Cancel</button>
              <button
                onClick={saveEditAttendance}
                disabled={editSaving || editLoading}
                data-testid='save-edit-attendance'
                className='px-4 py-2 rounded-lg bg-[#7EC845] text-white font-medium hover:bg-[#6DB534] disabled:opacity-50 flex items-center gap-2'
              >
                <Save size={16} /> {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Attendance;